import { useState } from "react";
import { Sparkles, Loader2, Copy, Check, FileText } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const SECTORS = [
  "business", "fitness", "e-commerce", "food", "fashion", "tech",
  "travel", "education", "beauty", "real-estate", "automotive", "entertainment",
];

const COUNTRIES = [
  { code: "BE", label: "België" },
  { code: "NL", label: "Nederland" },
  { code: "FR", label: "Frankrijk" },
  { code: "DE", label: "Duitsland" },
  { code: "GB", label: "Verenigd Koninkrijk" },
  { code: "US", label: "Verenigde Staten" },
];

const PLATFORMS = [
  "TikTok", "Instagram Reels", "YouTube Shorts", "Facebook Reels",
];

const TONES = [
  "energiek, direct", "professioneel", "casual & grappig",
  "educatief", "emotioneel", "controversieel",
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

const ScriptGeneratorDialog = ({ open, onOpenChange, generateScript, isDark }) => {
  const [form, setForm] = useState({
    hook_text: "",
    sector: "",
    country: "",
    target_audience: "",
    duration_seconds: 30,
    platform: "TikTok",
    tone: "energiek, direct",
  });
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setErr(null);
    setLoading(true);
    try {
      const res = await generateScript({
        hook_text: form.hook_text || null,
        sector: form.sector || null,
        country: form.country || null,
        target_audience: form.target_audience || null,
        duration_seconds: Number(form.duration_seconds) || 30,
        platform: form.platform || null,
        tone: form.tone || null,
      });
      setScript(res?.script || null);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Genereren mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (next) => {
    if (!next) {
      setScript(null);
      setErr(null);
    }
    onOpenChange(next);
  };

  const formatScript = (s) => {
    if (!s) return "";
    const lines = [];
    lines.push(`HOOK: ${s.hook || ""}`);
    lines.push("");
    if (Array.isArray(s.body_points)) {
      lines.push("BODY:");
      s.body_points.forEach((p, i) => lines.push(`${i + 1}. ${p}`));
      lines.push("");
    }
    lines.push(`CTA: ${s.cta || ""}`);
    if (s.notes) {
      lines.push("");
      lines.push(`NOTITIES: ${s.notes}`);
    }
    return lines.join("\n");
  };

  const copyScript = async () => {
    if (!script) return;
    try {
      await navigator.clipboard.writeText(formatScript(script));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      // ignore
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Script Generator
          </DialogTitle>
          <DialogDescription>
            Claude schrijft een volledig shortform-script (hook + body + CTA) gebaseerd op winnende patronen uit je bibliotheek.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Hook (optioneel, anders genereert AI er één)">
            <Input
              value={form.hook_text}
              onChange={(e) =>
                setForm((s) => ({ ...s, hook_text: e.target.value }))
              }
              placeholder="bv. 'Niemand vertelt je dit over...'"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Platform">
              <Select
                value={form.platform}
                onValueChange={(v) => setForm((s) => ({ ...s, platform: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Duur (sec)">
              <Input
                type="number"
                min="10"
                max="180"
                value={form.duration_seconds}
                onChange={(e) =>
                  setForm((s) => ({ ...s, duration_seconds: e.target.value }))
                }
              />
            </Field>

            <Field label="Sector">
              <Select
                value={form.sector || "none"}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, sector: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger><SelectValue placeholder="Kies sector" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Algemeen</SelectItem>
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Land">
              <Select
                value={form.country || "none"}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, country: v === "none" ? "" : v }))
                }
              >
                <SelectTrigger><SelectValue placeholder="Kies land" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Doelgroep">
            <Input
              value={form.target_audience}
              onChange={(e) =>
                setForm((s) => ({ ...s, target_audience: e.target.value }))
              }
              placeholder="bv. 'Jonge ondernemers, 25-35'"
            />
          </Field>

          <Field label="Tone">
            <Select
              value={form.tone}
              onValueChange={(v) => setForm((s) => ({ ...s, tone: v }))}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        {err && (
          <div
            className={cn(
              "rounded-md border p-2 text-[12px]",
              isDark ? "bg-red-500/[0.08] border-red-500/20 text-red-300" : "bg-red-50 border-red-200 text-red-700"
            )}
          >
            {err}
          </div>
        )}

        {script && (
          <div
            className={cn(
              "rounded-xl border p-4 space-y-3",
              isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50/50 border-gray-200/70"
            )}
          >
            <div className="flex items-center justify-between">
              <p
                className={cn(
                  "text-[11px] font-semibold uppercase tracking-wider",
                  isDark ? "text-indigo-400" : "text-indigo-600"
                )}
              >
                Gegenereerd script
                {script.estimated_duration && (
                  <span className={cn("ml-2 font-normal", isDark ? "text-gray-500" : "text-gray-500")}>
                    ~{script.estimated_duration}s
                  </span>
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={copyScript}
                className="h-7 gap-1 text-[11px]"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3 text-emerald-500" />
                    Gekopieerd
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" />
                    Kopieer alles
                  </>
                )}
              </Button>
            </div>

            <div>
              <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1", isDark ? "text-emerald-400" : "text-emerald-600")}>
                Hook
              </p>
              <p className={cn("text-[13px] font-medium", isDark ? "text-white" : "text-gray-900")}>
                {script.hook}
              </p>
            </div>

            {Array.isArray(script.body_points) && script.body_points.length > 0 && (
              <div>
                <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1", isDark ? "text-blue-400" : "text-blue-600")}>
                  Body
                </p>
                <ol className="space-y-1 list-decimal list-inside">
                  {script.body_points.map((p, i) => (
                    <li key={i} className={cn("text-[12px]", isDark ? "text-gray-300" : "text-gray-700")}>
                      {p}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {script.cta && (
              <div>
                <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1", isDark ? "text-purple-400" : "text-purple-600")}>
                  CTA
                </p>
                <p className={cn("text-[12px] font-medium", isDark ? "text-gray-200" : "text-gray-800")}>
                  {script.cta}
                </p>
              </div>
            )}

            {script.notes && (
              <div>
                <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1", isDark ? "text-gray-500" : "text-gray-500")}>
                  Productie notities
                </p>
                <p className={cn("text-[12px] italic", isDark ? "text-gray-400" : "text-gray-600")}>
                  {script.notes}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Sluiten
          </Button>
          <Button onClick={handleGenerate} disabled={loading}>
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {script ? "Opnieuw genereren" : "Genereer script"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScriptGeneratorDialog;
